import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dateFormat from 'dateformat';

import ErrorMessage from '../components/psc/lib/ErrorMessage'
import SaveButton from '../components/psc/lib/SaveButton'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import { PscPhrase } from '../components/psc/psc-phrase';
import FilePaths, { FileButtons } from '../components/psc/lib/FilePaths'
import UserSelect from '../components/psc/lib/UserSelect'
import TextArea from '../components/psc/lib/TextArea'
import PickDate from '../components/psc/lib/PickDate'
import Title from '../components/psc/lib/Title'
import DocumentVersion from '../components/psc/lib/DocumentVersion'
import Applicability from '../components/psc/lib/Applicability'
import Classification from '../components/psc/lib/Classification'
import CustomFields from '../components/psc/lib/CustomFields'
import HeaderStatusInfo from '../components/psc/lib/HeaderStatusInfo'
import Status from '../components/psc/lib/Status'
import RelatedItems from '../components/psc/lib/RelatedItems'
import Loading from '../components/psc/lib/Loading'
import NoAccess from '../components/psc/lib/NoAccess'

import useAuthentication from '../hooks/useAuthentication'
import useUserManagement from '../hooks/useUserManagement'
import useCadmObject from '../hooks/useCadmObject'
import useDocumentStatuses from '../hooks/useDocumentStatuses'
import useProjectContents from '../hooks/useProjectContents'

const IncludedDocument = () => {
    const navigate = useNavigate()
    const pscState = useSelector(state => state.pscState.value);
    const project = pscState.project
    const params = useParams();
    const isNew = params.objID === 'new';
    const projectID = Number(params.projectID);
    const objID = Number(params.objID);

    const { currentUser } = useAuthentication();

    const {
        userHasExplicitProjectAccess,
        userHasProjectAccessLevel,
        userHasProjectInformationAccess
    } = useUserManagement()

    const {
        initCustomFields,
        stdDocumentMap,
        getStdDocument
    } = useCadmObject('documents')

    const {
        documentStatuses,
    } = useDocumentStatuses()
    
    const {
        updateIncludedDocument,
        deleteIncludedDocument,
    } = useProjectContents()
    
    const [id, setId] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [ documentModels, setDocumentModels ] = useState([])
    const [ stdDocumentID, setStdDocumentID ] = useState(null)

    const [isUsed, setIsUsed] = useState(false);
    const [error, setError] = useState(false);
    const [original, setOriginal] = useState({ document_status_id: 1, custom_fields: [], project_type: {}, file_paths: [] });
    const [hasAccess, setHasAccess] = useState(true);
    const [originalProject, setOriginalProject] = useState({ project_type: {}, id: null, name: '' });
    const [deliveryLists, setDeliveryLists] = useState([]);
    const [documentStatusName, setDocumentStatusName ] = useState('')
    const [ relatedItems, setRelatedItems ] = useState([])
    const [previousVersionRelatedItems, setPreviousVersionRelatedItems] = useState([])
    const [hasLoaded, setHasLoaded] = useState(isNew ? true: false)
    const [submitActive, setSubmitActive] = useState(false);
    const [submitSpinner, setSubmitSpinner] = useState(false)

    useEffect(() => {
        if (isNew) {
            return
        }
        
        fetchObject()
    }, [objID, isNew])

    const stdDocument = useMemo(() => {
        return getStdDocument(stdDocumentID)
    }, [stdDocumentMap, stdDocumentID]);

    useEffect(() => {
        const status = documentStatuses.find((element) => element.id === original.document_status_id)
        setDocumentStatusName(status ? status.name : "")
    }, [documentStatuses, original.document_status_id])
    
    const fetchObject = () => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        fetch('/v0/included_documents/' + objID, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }
                // Update all the state values from json.result
                setOriginal(json.result.original)
                setOriginalProject(json.result.original_project)
                setStdDocumentID(json.result.std_document_id)
                setDocumentModels(json.result.document_models)
                setRelatedItems(json.result.related_items)
                setPreviousVersionRelatedItems(json.result.previous_version_related_items)
                setIsUsed(json.result.is_used)
                setUpdatedAt(json.result.updated_at)
                setHasLoaded(true)
            })
            .catch(err => {
                console.log('Included Document fetch error', err)
                setHasAccess(false)
            })

        fetch('/v0/delivery_lists?delivery_list_status_id=2&included_document_id=' + objID + '&project_id=' + projectID, { credentials: 'same-origin' })
            .then(function(response) {
                return response.json()
            })
            .then(function(json) {
                setDeliveryLists(json.results)
            })

    }

    // Convert object to lowercase json
    const toBackend = () => {
        return {
            project_id: projectID,
            document_models: documentModels,
            related_items: relatedItems,
            updated_at: updatedAt,
        }
    }

    const originalReferenceStructure = useMemo(() => {
        const scheme = originalProject.project_configuration_scheme
        if( ! scheme ) {
            return ''
        }
        const structure = scheme.reference_structures
            .find(function (d) { return d.cadm_object.href === 'documents' })
            .structure
        return structure
    }, [originalProject])
    
    const hasIssue = useMemo(() => {
        const structure = originalReferenceStructure
        if(structure.match(/\$Issue/) === null) {
            return false
        }
        return true
    }, [originalReferenceStructure])

    const hasRevision = useMemo(() => {
        const structure = originalReferenceStructure
        if(structure.match(/\$Revision/) === null) {
            return false
        }
        return true
    }, [originalReferenceStructure])
    
    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive  ) {
            return null
        }

        setSubmitActive(true)

        let submit = {
            objType: 'included_documents',
            objID: objID,
            data: toBackend(),
            successCallback: function(json) {
                updateIncludedDocument(json.result)
            }
        }
        return submit
    }
    
    const actionFork = (event) => {
        if( event ) {
            event.preventDefault()
        }
        const state = {
            forked_from_inc_doc_id: objID,
            forked_from_id: original.id,
            forked_from: {
                document_reference: original.reference,
                document_title: original.title,
            },
            project_id: projectID,
            std_document_id: stdDocument.id,
            title: original.title,
            classification: original.classification,
            note: original.note,
            owner_user_id: original.owner_user_id,
            document_models: documentModels,
            included_in_projects: [],
            final_version_file_path: '',
            file_paths: [],
            custom_fields: [],
        }
        navigate(`/portfolio/${projectID}/documents/new`, { state });
    }

    const renderDeliveryLists = () => {
        if (!deliveryLists) {
            return null
        }
        return (
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <label className="control-label">This Included Document was in the following Deliveries</label>
                    
                    <br/>
                    <small className="form-info-text" > Only Deliveries from this {project.project_type?.name} are listed here.</small>
                    
                    <ul className="list-group clear-list m-t">
                        {deliveryLists.length === 0 &&
                         <li className="list-group-item">
                             No Deliveries
                         </li>
                        }
                        {deliveryLists.length > 0 && deliveryLists.map(function(item) {
                            return (
                                <li key={item.id} className="list-group-item">
                                    <Link to={`/portfolio/${item.project_id}/delivery_lists/${item.id}`}>
                                        {dateFormat(item.sent_date, 'dd-mm-yyyy')} - {item.title}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        )
    }

    const renderForks = () => {
        if (! original.forks ) {
            return null
        }
        return (
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <label className="control-label">This Included Document was Forked to the following Documents</label>
                    
                    <br/>
                    <small className="form-info-text" > Only Forks within this {project.project_type?.name} are listed here.</small>
                    
                    <ul className="list-group clear-list m-t">
                        {original.forks.length === 0 &&
                         <li className="list-group-item">
                             No Forks
                         </li>
                        }
                        {original.forks.length > 0 && original.forks.map(function(item) {
                            return (
                                <li key={item.document_id} className="list-group-item">
                                    <Link to={`/portfolio/${item.project_id}/documents/${item.document_id}`}>
                                        {item.document_reference} - {item.document_title}
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        )
    }
    
    let forkButton = []
    if(userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')) {
        forkButton.push(
            <button key="fork-button" className="btn btn-link pull-right" type="button" onClick={actionFork}
                    data-tooltip-id="gnctooltip" data-tooltip-content="Fork to New Document">
                <i className="fa fa-code-fork" aria-hidden="true"></i>
            </button>
        )
    }
    
    let deleteButton = []
    if ( !isNew && userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER') && !isUsed) {
        var url = '/v0/included_documents/' + objID;
        deleteButton.push(
            <DeleteButton
                url={url}
                onError={handleSubmitError}
                callback={() => deleteIncludedDocument(objID)}
                extraText="Delete removes the Included item from this Project (as though it was never Included) - except from existing Tracking Report Baselines. Delete will not delete this item from the sharing Project."
            />
        )
    }

    let originalDocumentText = (
        <div key="originalText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
            <i className="fa fa-share-alt" aria-hidden="true"></i> &nbsp;
            This Document is owned by {originalProject.project_type?.name}: {originalProject.name}
        </div>
    )
    if (userHasProjectInformationAccess(currentUser,  originalProject.id, originalProject.project_type?.name )) {
        originalDocumentText = (
            <div key="originalText1" style={{color: 'rgba(103, 106, 108, 0.65)'}}>
                <i className="fa fa-share-alt" aria-hidden="true"></i> &nbsp;
                This Document is owned by {originalProject.project_type?.name}: &nbsp;
                <Link to={`/portfolio/${originalProject.id}/dashboard`}>
                    {originalProject.name}
                </Link>
            </div>
        )
    }
    
    if (!hasAccess) {
        return <NoAccess />
    }

    if (!hasLoaded) {
        return <Loading />
    }

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            
                            <HeaderStatusInfo
                                type="Document"
                                id={id}
                                status={documentStatusName}
                            />
                            {originalDocumentText}
                            
                            <Title
                                type="Included Document"
                                reference={original.reference}
                                title={original.title}
                                id={id}
                                showLatest={false}
                            />

                            <FileButtons
                                finalVersionFilePath={original.final_version_file_path}
                                buttonClass={original.document_status_id === 3 ? 'btn-warning' : ''}
                                visible={original.document_status_id === 3 || original.document_status_id === 4}
                            />
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(event) => handleSubmit(event)}>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_STD_DOCUMENT')}
                                        <br/>
                                        <small style={{fontWeight: 'normal'}}>(This {project.project_type?.name})</small>
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" disabled={true}
                                               value={stdDocument.name} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_REFERENCE')}</label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="reference"
                                               value={original.reference}
                                               disabled={true}
                                        />
                                        <span className="help-block m-b-none m-t-none">
                                            <small className="form-info-text" > {PscPhrase('DOCUMENT_CREATED_AUTOMATICALLY')}</small>
                                        </span>
                                    </div>
                                </div>

                                <DocumentVersion
                                    canIssue={hasIssue}
                                    canRevision={hasRevision}
                                    issue={original.issue}
                                    revision={original.revision}
                                />

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('LABEL_TITLE')}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="title" value={original.title} disabled={true} required />
                                    </div>
                                </div>

                                <Classification
                                    value={original.classification_id}
                                    disabled
                                />
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('DOCUMENT_LABEL_NOTE')}</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control" name="note" value={original.note} disabled={true} />
                                        <span className="help-block m-b-none m-t-none" >
                                            <small className="form-info-text" > {PscPhrase('DOCUMENT_INFO_NOTE')}</small>
                                        </span>

                                    </div>
                                </div>

                                <CustomFields
                                    customFields={original.custom_fields}
                                    cadmObjectType='documents'
                                    disabled={true}
                                />

                                <UserSelect
                                    label={PscPhrase('DOCUMENT_LABEL_OWNER')}
                                    name="owner_user_id"
                                    value={original.owner_user_id}
                                    disabled={true}
                                    projectId={projectID}
                                />

                                <Applicability
                                    projectId={projectID}
                                    projectType={project.project_type?.name}
                                    options={project.project_systems ?? []}
                                    value={documentModels}
                                    onChange={(value) => setDocumentModels(value)}
                                    disabled={!userHasProjectAccessLevel(currentUser, projectID, 'CADM_CONTROLLER')}
                                    subLabel={`(This ${project.project_type?.name})`}
                                />

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">{PscPhrase('DOCUMENT_LABEL_ISSUE_DATE')}
                                    </label>
                                    <div className="col-sm-8">
                                        <PickDate
                                            value={original.issue_date}
                                            disabled={true}
                                        />
                                    </div>
                                </div>

                                <Status
                                    value={original.document_status_id}
                                    statuses={documentStatuses}
                                    disabled={true}
                                />

                                <div className="hr-line-dashed"></div>

                                <FilePaths
                                    disabled
                                    filePaths={original.file_paths}
                                    finalVersionFilePath={original.final_version_file_path}
                                    inUse={original.document_status_id === 3 || original.document_status_id === 4}
                                    showFileLabels
                                    buttonClass={original.document_status_id === 3 ? 'btn-warning' : ''}
                                    displayOriginal
                                />

                                <div className="hr-line-dashed"></div>

                                <RelatedItems
                                    object_type="included_documents"
                                    object_reference_without_version={original.reference_without_version}
                                    project_id={projectID}
                                    project_type={project.project_type?.name}
                                    related_items={relatedItems}
                                    previous_version_related_items={previousVersionRelatedItems}
                                    disabled={ ! userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER") }
                                    onChange={(value) => setRelatedItems(value)}
                                />

                                <div className="hr-line-dashed"></div>

                                {renderDeliveryLists()}

                                <div className="hr-line-dashed"></div>

                                {renderForks()}
                                
                                <div className="hr-line-dashed"></div>
                                
                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />
                                    
                                    <div className="col-sm-8 col-sm-offset-2">
                                        <CancelButton />
                                        
                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userHasProjectAccessLevel(currentUser,projectID, "CADM_CONTROLLER")}
                                            spinnerActive={submitSpinner}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />
                                        
                                        {deleteButton}
                                        
                                        {forkButton}
                                    </div>
                                </div>
                                
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default IncludedDocument
